import React, { useState, useEffect } from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import { Heart, CreditCard, Building, Globe, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import arcaPaymentService from '../services/arcaPaymentService';

const DonatePage = () => {
  const [showWidget, setShowWidget] = useState(true);
  const [isWidgetLoading, setIsWidgetLoading] = useState(true);
  const [preloadWidget, setPreloadWidget] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    amount: '',
    customAmount: '',
    donationType: 'one-time',
    paymentMethod: 'card',
    message: '',
    isAnonymous: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const predefinedAmounts = [25, 50, 100, 250, 500, 1000];

  // Load supported currencies on component mount
  useEffect(() => {
    loadCurrencies();

    // Start preloading donation widget after short delay
    const timer = setTimeout(() => {
      setPreloadWidget(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const loadCurrencies = async () => {
    const result = await arcaPaymentService.getSupportedCurrencies();
    if (result.success && result.data.data) {
      setCurrencies(result.data.data);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAmountSelect = (amount) => {
    setFormData(prev => ({ ...prev, amount: amount.toString(), customAmount: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate amount
    const donationAmount = parseFloat(formData.customAmount || formData.amount);
    if (!donationAmount || donationAmount <= 0) {
      setError('Please enter a valid donation amount');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Show embedded Givebutter widget instead of redirecting
    setIsWidgetLoading(true);
    setShowWidget(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PublicLayout>
      <SEO
        title={seoConfig.donate.title}
        description={seoConfig.donate.description}
        keywords={seoConfig.donate.keywords}
        url={seoConfig.donate.url}
      />
      {/* Hero Section */}
      <section className="position-relative text-white py-4 arca-gradient">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <Heart className="mb-2" size={48} style={{ fill: 'currentColor' }} />
              <h1 className="h2 fw-bold mb-2">Support ARCA's Mission</h1>
              <p className="mb-0">
                Your donation helps us provide essential respiratory care training, equipment, and resources across Africa.
              </p>
              <div className="mt-3 p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}>
                <p className="mb-0 fw-semibold" style={{ fontSize: '0.95rem' }}>
                  <i className="fas fa-certificate me-2"></i>
                  ARCA is a 501(c)(3) tax-exempt organization | EIN: 33-4409738
                </p>
                <small className="d-block mt-1" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                  Your donation is tax-deductible to the fullest extent allowed by law
                </small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Form Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Success Message */}
              {success && (
                <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
                  <div className="d-flex align-items-center">
                    <CheckCircle className="me-2" size={24} />
                    <div>
                      <h5 className="alert-heading mb-1">Thank You for Your Donation!</h5>
                      <p className="mb-0">Your generous contribution will help us train healthcare professionals and save lives across Africa.</p>
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setSuccess(false)} aria-label="Close"></button>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                  <div className="d-flex align-items-center">
                    <AlertCircle className="me-2" size={24} />
                    <div>
                      <strong>Error:</strong> {error}
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
                </div>
              )}

              <div className="card shadow-lg border-0">
                <div className="card-body p-4 p-lg-5">
                  {!showWidget ? (
                    <>
                      <h2 className="h3 fw-bold text-center mb-4">Make a Donation</h2>

                      <form onSubmit={handleSubmit}>
                        {/* Donation Type */}
                        <div className="mb-4">
                          <label className="form-label fw-bold">Donation Type</label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="donationType"
                                id="one-time"
                                value="one-time"
                                checked={formData.donationType === 'one-time'}
                                onChange={handleChange}
                              />
                              <label className="form-check-label" htmlFor="one-time">
                                One-Time
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="donationType"
                                id="monthly"
                                value="monthly"
                                checked={formData.donationType === 'monthly'}
                                onChange={handleChange}
                              />
                              <label className="form-check-label" htmlFor="monthly">
                                Monthly
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Amount Selection */}
                        <div className="mb-4">
                          <label className="form-label fw-bold">Select Amount (USD)</label>
                          <div className="row g-2 mb-3">
                            {predefinedAmounts.map(amount => (
                              <div key={amount} className="col-4 col-md-2">
                                <button
                                  type="button"
                                  onClick={() => handleAmountSelect(amount)}
                                  className={`btn w-100 fw-bold ${formData.amount === amount.toString()
                                    ? 'btn-primary'
                                    : 'btn-outline-primary'
                                    }`}
                                >
                                  ${amount}
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              type="number"
                              name="customAmount"
                              value={formData.customAmount}
                              onChange={(e) => setFormData(prev => ({ ...prev, customAmount: e.target.value, amount: '' }))}
                              placeholder="Enter custom amount"
                              min="1"
                              className="form-control"
                            />
                          </div>
                        </div>

                        {/* Personal Information */}
                        <h5 className="fw-bold mb-3">Your Information</h5>
                        <div className="row g-3 mb-4">
                          <div className="col-md-6">
                            <label className="form-label">First Name *</label>
                            <input
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              className="form-control"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Last Name *</label>
                            <input
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              className="form-control"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Email *</label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              className="form-control"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Phone</label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className="form-control"
                            />
                          </div>
                        </div>

                        {/* Payment Method */}
                        <div className="mb-4">
                          <label className="form-label fw-bold">Payment Method</label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="paymentMethod"
                                id="card"
                                value="card"
                                checked={formData.paymentMethod === 'card'}
                                onChange={handleChange}
                              />
                              <label className="form-check-label d-flex align-items-center gap-2" htmlFor="card">
                                <CreditCard size={18} />
                                Credit/Debit Card
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="paymentMethod"
                                id="bank"
                                value="bank"
                                checked={formData.paymentMethod === 'bank'}
                                onChange={handleChange}
                              />
                              <label className="form-check-label d-flex align-items-center gap-2" htmlFor="bank">
                                <Building size={18} />
                                Bank Transfer
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Message */}
                        <div className="mb-4">
                          <label className="form-label">Message (Optional)</label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            className="form-control"
                            rows="3"
                            placeholder="Leave a message with your donation..."
                          />
                        </div>

                        {/* Anonymous Donation Option */}
                        <div className="mb-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="isAnonymous"
                              id="isAnonymous"
                              checked={formData.isAnonymous}
                              onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                            />
                            <label className="form-check-label" htmlFor="isAnonymous">
                              Make this donation anonymous
                            </label>
                          </div>
                        </div>

                        {/* Submit Button */}
                        <button
                          type="submit"
                          className="btn btn-primary btn-lg w-100 fw-bold"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Loader size={20} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Heart size={20} className="me-2" style={{ fill: 'currentColor' }} />
                              Complete Donation
                            </>
                          )}
                        </button>
                      </form>
                    </>
                  ) : (
                    <>
                      {/* Givebutter Embedded Widget */}
                      <div className="alert alert-info mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Complete your donation securely below. Thank you for supporting ARCA's mission!
                      </div>

                      {/* Loading Indicator */}
                      {isWidgetLoading && (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading donation form...</span>
                          </div>
                          <p className="mt-3 text-muted">Loading secure donation form...</p>
                        </div>
                      )}

                      {/* Givebutter Widget Container */}
                      <div
                        className="givebutter-embed"
                        style={{
                          width: '100%',
                          minHeight: '700px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          display: isWidgetLoading ? 'none' : 'block'
                        }}
                      >
                        <iframe
                          src="https://givebutter.com/embed/c/PgJiN6"
                          width="100%"
                          height="700px"
                          style={{
                            maxWidth: '100%',
                            border: 'none'
                          }}
                          name="givebutter"
                          frameBorder="0"
                          scrolling="yes"
                          seamless
                          allowpaymentrequest="true"
                          title="Givebutter Donation Widget"
                          onLoad={() => setIsWidgetLoading(false)}
                        />
                      </div>

                      <div className="text-center mt-3">
                        <small className="text-muted d-block mb-2">
                          <i className="fas fa-shield-alt me-1"></i>
                          Secure donation powered by Givebutter
                        </small>
                        <div className="alert alert-info py-2 mb-0" style={{ fontSize: '0.875rem' }}>
                          <i className="fas fa-info-circle me-1"></i>
                          <strong>Tax Deductible:</strong> ARCA is a 501(c)(3) organization (EIN: 33-4409738)
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Thank You Message Section */}
      <section className="py-4 bg-white">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <Heart className="text-danger mb-3" size={48} style={{ fill: 'currentColor' }} />
              <h2 className="h4 fw-bold text-dark mb-3">Thank You for Your Support</h2>
              <p className="text-muted mb-0">
                Your support helps ARCA achieve our mission and vision of advancing respiratory care across Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden Preload Iframe - Loads widget in background for instant display */}
      {preloadWidget && !showWidget && (
        <iframe
          src="https://givebutter.com/embed/c/PgJiN6"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            top: '-9999px',
            left: '-9999px',
            opacity: 0,
            pointerEvents: 'none'
          }}
          title="Preload Donation Widget"
          aria-hidden="true"
        />
      )}
    </PublicLayout>
  );
};

export default DonatePage;